import type { RecommendationInfo, RecommendationsResponse } from "./types";

/**
 * `recommendations/attribution` — stamping "this came from a suggestion" onto a sale item.
 *
 * The stamp is the ONLY link between the widget and the revenue it produces: the cart analytics
 * effect, the GA funnel and the engine's own feedback loop all key off `saleItem.recommendationInfo`.
 * It survives a repeat-modal round trip and a customiser round trip, which is why it is a value
 * that gets carried rather than a flag set at the moment of adding.
 */

/** The attribution for the CURRENT engine response. */
export function createRecommendationInfo(
  recommendations: RecommendationsResponse | undefined,
  recommendationProvider?: string,
): RecommendationInfo {
  return {
    recommendationId: recommendations?.recommendationId,
    recommendationProvider,
  };
}

/**
 * Copy `recommendationInfo` from a clicked recommendation onto the object that is about to
 * become (or update) a sale item.
 *
 * A no-op when there is nothing to attribute, so it is safe to call unconditionally on the
 * catalogue path too — which is what keeps the customiser and repeat-modal call sites free of
 * `if (recommendationInfo)` noise.
 */
export function attachRecommendationInfo<T extends Record<string, any>>(
  target: T,
  recommendationInfo?: RecommendationInfo,
): T {
  if (!target || !recommendationInfo?.recommendationId) return target;
  (target as any).recommendationInfo = recommendationInfo;
  return target;
}

/** Was this sale item added from a suggestion? */
export function isRecommendedSaleItem(saleItem: any): boolean {
  return Boolean(saleItem?.recommendationInfo?.recommendationId);
}
